import { ButtonHTMLAttributes } from 'react';
import { paradigmLightBlue } from '@paradigm/design-system/src/colors';
import { getOppositeSide } from '@paradigm/features/src/unified-rfqs/ui/rfqs/quotes-functions';
import {
  EmptyPrice,
  AskPriceButton,
  BidPriceButton,
} from '#/components/rfq-common/ActionButtons';
import { useUMPlatformCreationDisabled } from '@paradigm/features/src/platform-state/contexts/providers';
import type { RfqMarkPrice } from '@paradigm/features/src/unified-rfqs/ui/rfq-websocket/mark-price-context';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly price: string | undefined;
  readonly formattedPrice: string;
  // Show same mark price borders as quote to be crossed
  readonly isAggressPrice?: boolean;
  readonly side: 'BUY' | 'SELL';
  readonly isLockedQuote?: boolean;
  readonly isMarkOrBetter: RfqMarkPrice['isMarkOrBetter'];
  readonly productCode?: string;
}

export default function PriceButtonWithMark({
  price,
  formattedPrice,
  side,
  isMarkOrBetter,
  productCode,
  isAggressPrice = false,
  isLockedQuote = false,
  ...props
}: Props) {
  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();
  const markSide = isAggressPrice ? getOppositeSide(side) : side;
  const isBetterOrEqual = isMarkOrBetter(price, markSide);
  const ariaLabel = isBetterOrEqual ? 'mark_or_better' : '';
  if (price == null) {
    return <EmptyPrice disabled={isPlatformCreationDisabled} {...props} />;
  }

  if (side === 'SELL')
    return (
      <AskPriceButton
        {...props}
        disabled={isPlatformCreationDisabled}
        aria-label={ariaLabel}
        css={getBorderCss(isBetterOrEqual, isLockedQuote)}
        price={formattedPrice}
      />
    );

  return (
    <BidPriceButton
      {...props}
      aria-label={ariaLabel}
      disabled={isPlatformCreationDisabled}
      css={getBorderCss(isBetterOrEqual, isLockedQuote)}
      price={formattedPrice}
    />
  );
}

function getBorderCss(isBetterOrEqual: boolean, isLockedQuote: boolean) {
  if (isBetterOrEqual && !isLockedQuote) {
    return `border: 1px solid ${paradigmLightBlue}`;
  }

  return undefined;
}
